/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    Drawer,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IDrawerState,
    IPayLoad,
    ITelemetryContent,
    Module,
    Node,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import { IImageData } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import { ICountrySelectorViewProps } from '../../../modules/country-selector/country-selector';
import { IMenuItemData } from '../../../modules/country-selector/country-selector.data';
import { ICountrySelectorConfig } from '../definition-extensions/country-selector.ext.props.autogenerated';

interface INavigationState {
    parentMenu?: number;
    activeMenu?: number;
    categoryImage?: IImageData[] | null;
    mobileViewLabelText?: string;
    categoryImageAltText: string;
}

/**
 *
 * NavigationMenuView component
 * @extends {React.PureComponent<ICountrySelectorViewProps>}
 */
export class CountrySelectorView extends React.PureComponent<ICountrySelectorViewProps, INavigationState> {
    private currentLevel: number = 0;
    private escapeKey: number = 27;
    private menuNode: React.RefObject<HTMLUListElement>;
    private telemetryContent: ITelemetryContent;
    private payLoad: IPayLoad;
    private resizeObserver: ResizeObserver | null = null;

    constructor(props: ICountrySelectorViewProps) {
        super(props);
        this.menuNode = React.createRef();
        this.state = {
            activeMenu: undefined,
            mobileViewLabelText: '',
            parentMenu: undefined,
            categoryImageAltText: ''
        };
        this._closeSubmenu = this._closeSubmenu.bind(this);
        this._escFunction = this._escFunction.bind(this);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.payLoad = getPayloadObject('click', this.telemetryContent, '', '');
    }

    private initializeResizeObserver = () => {
        this.resizeObserver = new ResizeObserver(_ => {
            const div = document.querySelector('.mobile.menus');
            if (div) {
                div.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
        });
    };

    public componentDidMount(): void {
        document.body && document.addEventListener('keydown', (this._escFunction as unknown) as EventListener, false);
        document.body && document.body.addEventListener('mousedown', this._handleClickOutside);
        this.initializeResizeObserver();
    }

    public componentWillUnmount(): void {
        document.body && document.removeEventListener('keydown', (this._escFunction as unknown) as EventListener, false);
        document.body && document.body.removeEventListener('mousedown', this._handleClickOutside, false);
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    public handleDrawerToggle = (e: IDrawerState) => {
        const mobileMenu = document.querySelector('.mobile.menus');
        if (e.isOpen && mobileMenu && this.resizeObserver) {
            this.resizeObserver.observe(mobileMenu);
        }
    };

    public render(): JSX.Element | null {
        const { isMobileView } = this.props;

        this.currentLevel = 1;
        return <React.Fragment>{isMobileView ? this._renderMobileMenu() : this._renderDesktopMenu()}</React.Fragment>;
    }

    private _renderMobileMenu(): JSX.Element {
        const { isMobileView, menuItemData, Navigation, config } = this.props;
        return (
            <Module {...Navigation} className={classnames(Navigation.className, isMobileView ? 'mobile-view' : 'desktop-view')}>
                {menuItemData.map((menuItem: IMenuItemData) => {
                    return this._renderDrawerMenu(menuItem, config);
                })}
            </Module>
        );
    }
    private _renderDesktopMenu(): JSX.Element {
        const { MenuList, Navigation } = this.props;
        return (
            <Module {...Navigation} className={classnames(Navigation.className)}>
                <Node {...MenuList} ref={this.menuNode} tabIndex='-1'>
                    {this._renderDisplay()}
                </Node>
            </Module>
        );
    }

    private _renderDrawerMenu(menuItem: IMenuItemData, config: ICountrySelectorConfig): JSX.Element | null {
        if (menuItem && menuItem.subMenu && menuItem.subMenu.length > 0) {
            return this._renderDrawer(menuItem, config);
        } else {
            return this._renderLinkMenuItem(menuItem);
        }
    }

    private _renderDrawer(menuItem: IMenuItemData, config: ICountrySelectorConfig): JSX.Element | null {
        const toggleCountrySelectorText = menuItem.subMenu?.find(e => e.imageSource === menuItem.imageSource)?.linkText || '';
        const toggleButtonText = `${menuItem.linkText}${toggleCountrySelectorText ? `: ${toggleCountrySelectorText}` : ''}`;
        // const toggleButtonImg = menuItem.imageSource;
        return (
            <Drawer
                onToggle={this.handleDrawerToggle}
                key={menuItem.id}
                className={'ms-nav__drawer'}
                // openGlyph="ms-nav__drawer-open"
                // closeGlyph="ms-nav__drawer-close"
                // glyphPlacement="end"
                toggleButtonText={this._renderLinkText(toggleButtonText)}
            >
                <div className='drawer__submenu'>
                    {config.countrySelectorIntroText && <h4 className={'menuIntroText'}>{config.countrySelectorIntroText}</h4>}
                    {menuItem.subMenu!.map((menuSubItem: IMenuItemData) => {
                        return this._renderDrawerLink(menuSubItem);
                    })}
                </div>
            </Drawer>
        );
    }

    private _renderLinkText(linkText: string | undefined, linkImg?: string): JSX.Element {
        return (
            <React.Fragment>
                {linkText}
                {linkImg && <img alt='flag' src={linkImg} />}
            </React.Fragment>
        );
    }

    private _renderDrawerLink(item: IMenuItemData): JSX.Element | null {
        if (item && item.linkText && item.linkURL && item.linkURL.length > 0) {
            return this._renderLinkMenuItem(item);
        } else if (item && item.linkText && !item.linkURL) {
            return this._renderSpanMenuItem(item);
        }
        return null;
    }

    private _renderDisplay(): JSX.Element[] {
        const { ListItem, menuItemData, isMobileView } = this.props;
        const { activeMenu } = this.state;
        const menuItemList: JSX.Element[] = [];

        if (isMobileView && activeMenu !== undefined && menuItemData.length > 0) {
            let menuItem: IMenuItemData = {};
            for (let i = 0; i < menuItemData.length; i++) {
                if (menuItemData[i] && menuItemData[i].id === activeMenu) {
                    menuItem = menuItemData[i];
                    this.setState({
                        parentMenu: undefined,
                        mobileViewLabelText: menuItemData[i].linkText
                    });
                    break;
                }
                menuItem = this._getFromSubMenu(menuItemData[i]) as IMenuItemData;
                if (menuItem && menuItem.id === activeMenu) {
                    break;
                }
            }

            menuItem &&
                menuItemList.push(
                    <Node key={menuItem.id} {...ListItem}>
                        {' '}
                        {this._createMenuItemList(menuItem)}{' '}
                    </Node>
                );
        } else {
            menuItemData.forEach((item: IMenuItemData, index: number) => {
                menuItemList.push(
                    <Node key={index} {...ListItem}>
                        {this._createMenuItemList(item)}
                    </Node>
                );
            });
        }

        return menuItemList;
    }

    private _getFromSubMenu(item?: IMenuItemData): IMenuItemData | null {
        const subMenus = item && item.subMenu;
        if (subMenus && subMenus.length > 0) {
            for (let i = 0; i <= subMenus.length - 1; i++) {
                if (subMenus[i].id === this.state.activeMenu) {
                    this.setState({
                        parentMenu: item && item.id,
                        mobileViewLabelText: subMenus[i].linkText
                    });
                    return subMenus[i];
                }
                const found = this._getFromSubMenu(subMenus[i]);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }

    private _createMenuItemList(menuItemData: IMenuItemData): JSX.Element | null {
        if (menuItemData && menuItemData.subMenu && menuItemData.subMenu.length > 0) {
            if (this.props.isMobileView && this.state.activeMenu !== undefined) {
                return this._renderSubMenu(menuItemData.subMenu, menuItemData.id);
            } else {
                return (
                    <React.Fragment>
                        {this._renderButtonMenuItem(menuItemData)}
                        {this._renderSubMenu(menuItemData.subMenu, menuItemData.id)}
                    </React.Fragment>
                );
            }
        } else if (menuItemData && menuItemData.linkText && menuItemData.linkURL && menuItemData.linkURL.length > 0) {
            return this._renderLinkMenuItem(menuItemData, menuItemData.id, false);
        } else if (menuItemData && menuItemData.linkText && !menuItemData.linkURL) {
            return this._renderSpanMenuItem(menuItemData);
        }

        return null;
    }

    private _renderSubMenu(subMenus?: IMenuItemData[], activeMenu?: number, IsSubMenu?: boolean): JSX.Element | null {
        const { isMobileView, ListItem } = this.props;
        const enableMultiSupportMenu = false;
        const multiLevelSupportedMenu = 2;
        // const isParentMenu:boolean= false;
        if (activeMenu && this.state.activeMenu !== activeMenu) {
            // this.props.context.telemetry.error(
            //   "Navigation Active menu content is empty, module wont render."
            // );
            return null;
        }

        if (!subMenus || subMenus.length === 0) {
            // this.props.context.telemetry.error(
            //   "Navigation Submenu content is empty, module wont render."
            // );
            return null;
        }

        let levelClassName: string = '';
        const menuOptions =
            subMenus &&
            subMenus.map((option: IMenuItemData, idx: number) => {
                const hasOptions = option.subMenu && option.subMenu.length > 0;
                let menuItem: JSX.Element | null;
                if (hasOptions && isMobileView) {
                    menuItem = this._renderButtonMenuItem(option, activeMenu, idx);
                } else {
                    menuItem = option.linkURL ? this._renderLinkMenuItem(option, idx) : this._renderSpanMenuItem(option);
                }
                let subMenu;
                const haveSubmenu = hasOptions && enableMultiSupportMenu && this.currentLevel <= Math.round(multiLevelSupportedMenu) - 1;
                if (haveSubmenu) {
                    this.currentLevel++;
                    levelClassName = enableMultiSupportMenu ? `level-${this.currentLevel.toString()}` : '';
                    subMenu = this._renderSubMenu(option.subMenu, isMobileView ? option.id : undefined, true);
                }
                return (
                    <Node {...ListItem} key={option.id} className={classnames(ListItem.className, haveSubmenu && 'havesubmenu')}>
                        {menuItem}
                        {subMenu}
                    </Node>
                );
            });
        return this._renderMenu(levelClassName, menuOptions, activeMenu, IsSubMenu);
    }

    private _renderButtonMenuItem(option: IMenuItemData, activeMenu?: number, index?: number): JSX.Element | null {
        const { Button } = this.props;
        return (
            <>
                {this.props.isMobileView && option.linkText}
                <Node
                    key={index}
                    {...Button}
                    className={classnames(Button.className, "country-selector-dropdown-button")}
                    onClick={this._handleDropdownToggle(option, activeMenu)}
                    onFocus={this._closeSubmenu}
                    aria-haspopup={true}
                    aria-expanded={this.state.activeMenu && this.state.activeMenu === option.id ? true : false}
                    data-parent={activeMenu}
                >
                    {option.imageSource && option.subMenu?.find(sub => [option.imageSource || ""].includes(sub.imageSource || ""))?.linkText}
                </Node>
            </>
            
        );
    }

    private _renderLinkMenuItem(option: IMenuItemData, index?: number, hoverEffect: boolean = true): JSX.Element | null {
        const { Link } = this.props;
        const linkText = option.linkText ? option.linkText : '';
        const imagesource = option.imageSource ? option.imageSource : '';
        this.payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        const currentSite = typeof window !== 'undefined' && `${location.origin}${this.props.context.request.sitePath}`;
        const currentLang = currentSite === option.linkURL ? true : false;
        return (
            <Node
                {...Link}
                key={index}
                href={option.linkURL}
                {...attributes}
                aria-current={currentLang && 'true'}
                className={`${currentLang && 'current'}  ${this.props.className}`}
                onClick={onTelemetryClick(this.telemetryContent, this.payLoad, linkText)}
            >
                {imagesource && <img alt={''} src={imagesource} />}
                <span>{option.linkText}</span>
            </Node>
        );
    }

    private _renderSpanMenuItem(option: IMenuItemData, index?: number): JSX.Element | null {
        const { Span } = this.props;
        return (
            <Node key={index} {...Span}>
                {option.linkText}
            </Node>
        );
    }

    private _renderMenu(level: string, menuOptions: JSX.Element[], currentItem?: number, submenu?: boolean): JSX.Element | null {
        const { DivContainer, MenuList, config } = this.props;
        const DivContainerClass = this.currentLevel > 2 ? DivContainer!.className : 'ms-nav__deafult';
        this.currentLevel = 1;
        return (
            <Node {...DivContainer} className={DivContainerClass}>
                <Node {...MenuList} className={classnames(MenuList.className, level)}>
                    {config.countrySelectorIntroText && <h4 className={'menuIntroText'}>{config.countrySelectorIntroText}</h4>}

                    {menuOptions}
                </Node>
            </Node>
        );
    }
    private _handleDropdownToggle = (data: IMenuItemData, parentId?: number) => () => {
        if (!this.props.isMobileView) {
            this.setState({
                activeMenu: this.state.activeMenu && this.state.activeMenu === data.id! ? undefined : data.id!,
                parentMenu: parentId
            });
        } else {
            this.setState({
                activeMenu: data.id,
                mobileViewLabelText: data.linkText!,
                parentMenu: parentId
            });
        }

        this._resetFocus();
    };

    private _resetFocus = () => {
        if (this.props.isMobileView) {
            setTimeout(() => {
                this.menuNode && this.menuNode.current && this.menuNode.current.focus();
            }, 0);
        }
    };

    // tslint:disable-next-line:no-any
    private _handleClickOutside = (event: any) => {
        if (this.menuNode.current && !this.menuNode.current.contains(event.target)) {
            this.setState({ activeMenu: undefined, mobileViewLabelText: '' });
        }
    };

    private _closeSubmenu(): void {
        if (!this.props.isMobileView) {
            this.setState({ activeMenu: undefined, mobileViewLabelText: '' });
        }
    }
    private _escFunction = (event: React.KeyboardEvent) => {
        if (event.keyCode === this.escapeKey) {
            this._closeSubmenu();
        }
    };
}

export default CountrySelectorView;
